import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ReactStars from 'react-stars';
import { createClient } from '@supabase/supabase-js';
import { MessageSquare, ThumbsUp, Calendar } from 'lucide-react';

// Initialize Supabase client
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

interface Review {
  id: string;
  rating: number;
  comment: string;
  created_at: string;
  service_type: string;
  profiles: {
    email: string;
  };
}

const Reviews: React.FC = () => {
  const [reviews] = useState<Review[]>([]);
  const [newReview, setNewReview] = useState({
    rating: 5,
    comment: '',
    service_type: 'Web Development'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchReviews();
  }, [filter]);

  const fetchReviews = async () => {
    let query = supabase
      .from('reviews')
      .select(`
        id,
        rating,
        comment,
        created_at,
        service_type,
        profiles (
          email
        )
      `)
      .eq('status', 'approved')
      .order('created_at', { ascending: false });

    if (filter !== 'all') {
      query = query.eq('service_type', filter);
    }

    const { error } = await query;

    if (error) {
      console.error('Error fetching reviews:', error);
      return;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      alert('Please sign in to submit a review');
      setIsSubmitting(false);
      return;
    }

    const { error } = await supabase
      .from('reviews')
      .insert([
        {
          user_id: user.id,
          ...newReview
        }
      ]);

    if (error) {
      alert('Error submitting review. Please try again.');
      console.error('Error:', error);
    } else {
      setNewReview({
        rating: 5,
        comment: '',
        service_type: 'Web Development'
      });
      alert('Review submitted successfully! It will be visible after approval.');
    }

    setIsSubmitting(false);
  };

  return (
    <section id="reviews" className="section bg-black relative">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
      
      <div className="container-custom px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-16">
          <h2 className="mb-4 sm:mb-6 text-2xl sm:text-4xl font-bold text-white">Client Reviews</h2>
          <p className="text-white/70 text-base sm:text-lg">
            See what our clients have to say about their experience working with us.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 sm:mb-12">
          {['all', 'Web Development', 'AI Agents', 'AI Automation', 'Data Solutions'].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 text-sm rounded-full transition-all ${
                filter === type
                  ? "bg-white text-black"
                  : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 gap-6 sm:gap-8 mb-8 sm:mb-16 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card group p-4 sm:p-6"
            >
              <div className="flex items-center gap-3 sm:gap-4 mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h4 className="font-medium text-sm sm:text-base">{review.profiles.email.split('@')[0]}</h4>
                  <p className="text-xs sm:text-sm text-white/60">{review.service_type}</p>
                </div>
              </div>
              
              <ReactStars
                count={5}
                value={review.rating}
                size={20} // Reduced size for mobile
                color2={'#ffffff'}
                edit={false}
              />
              
              <p className="mt-3 sm:mt-4 text-white/80 text-sm sm:text-base">{review.comment}</p>
              
              <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs sm:text-sm text-white/60">
                <div className="flex items-center gap-2 mb-2 sm:mb-0">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                  {new Date(review.created_at).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-2">
                  <ThumbsUp className="w-3 h-3 sm:w-4 sm:h-4" />
                  Verified Client
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Submit Review Form */}
        <div className="max-w-full sm:max-w-2xl mx-auto">
          <div className="card p-4 sm:p-6">
            <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Share Your Experience</h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Rating
                </label>
                <ReactStars
                  count={5}
                  value={newReview.rating}
                  size={28} // Slightly smaller for mobile
                  color2={'#ffffff'}
                  onChange={(rating) => setNewReview(prev => ({ ...prev, rating }))}
                />
              </div>
              
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Service Type
                </label>
                <select
                  value={newReview.service_type}
                  onChange={(e) => setNewReview(prev => ({ ...prev, service_type: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-1 focus:ring-white/30 text-white text-sm sm:text-base"
                >
                  <option value="Web Development">Web Development</option>
                  <option value="AI Agents">AI Agents</option>
                  <option value="AI Automation">AI Automation</option>
                  <option value="Data Solutions">Data Solutions</option>
                </select>
              </div>
              
              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Your Review
                </label>
                <textarea
                  value={newReview.comment}
                  onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-1 focus:ring-white/30 text-white text-sm sm:text-base h-24 sm:h-32 resize-none"
                  placeholder="Share your experience working with us..."
                  required
                  minLength={10}
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary w-full py-2 sm:py-3 text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Review'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;